import { Button } from '@progress/kendo-react-buttons';
import { ColumnMenuItem } from '@progress/kendo-react-data-tools';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    GridColumnMenuColumnsList,
    GridColumnMenuFilter,
    GridColumnMenuItemContent,
    GridColumnMenuProps,
    GridColumnMenuSort
} from '@progress/kendo-react-grid';
import { columnsIcon, filterClearIcon, filterIcon, lockIcon, undoIcon, unlockIcon } from '@progress/kendo-svg-icons';
import React from 'react';
import { AirportFlightCategory, FlightStatus } from '../../../common/enums';
import { MonitorStatusOptions } from '../../../types/dropdown-options';
import ColumnCheckboxFilter from '../../components/grid/ColumnCheckboxFilter';
import { useMultiWindowContext } from '../multi-window/context/multi-window-context';
import { ColumnMenuContext } from './ColumnMenuContext';
import { NotesColumnFilters } from './NotesColumnFilter';

const flightCategoryData = [
    { text: 'Unknown', value: AirportFlightCategory.unknown },
    { text: 'VFR', value: AirportFlightCategory.VFR },
    { text: 'MVFR', value: AirportFlightCategory.MVFR },
    { text: 'IFR', value: AirportFlightCategory.IFR },
    { text: 'LIFR', value: AirportFlightCategory.LIFR },
    { text: 'BCAT1', value: AirportFlightCategory.BCAT1 }
];

const flightStatusData = [
    { text: 'Active', value: FlightStatus.Active },
    { text: 'Aged-out', value: FlightStatus.Agedout },
    { text: 'Arrived', value: FlightStatus.Arrived },
    { text: 'Hold', value: FlightStatus.Hold },
    { text: 'Scheduled', value: FlightStatus.Scheduled },
    { text: 'Planned', value: FlightStatus.Planned },
    { text: 'Departed', value: FlightStatus.Departed },
    { text: 'New record', value: FlightStatus.NewRecord },
    { text: 'Not valid', value: FlightStatus.NotValid }
];

const checkboxFilterColumns = ['metarFc', 'flightStatus', 'monStat', 'MonStatApt', 'MonStatOrg', 'MonStatDst', 'MonStatAlt'];

export const ColumnMenu = (props: GridColumnMenuProps) => {
    const multiWindow = useMultiWindowContext();
    const [showColumnResetPopup, setShowColumnResetPopup] = React.useState(false);
    const { onCloseMenu = () => {} } = props;
    const context = React.useContext(ColumnMenuContext);
    const [expandedColumnMenu, setExpandColumnMenu] = React.useState(false);
    const [expandFilter, setExpandFilter] = React.useState(false);

    const getColumnFilterData = () => {
        switch (props.column.id) {
            case 'metarFc':
                return flightCategoryData;
            case 'flightStatus':
                return flightStatusData;
            case 'monStat':
            case 'MonStatApt':
            case 'MonStatOrg':
            case 'MonStatDst':
            case 'MonStatAlt':
                return MonitorStatusOptions;
            default:
                return [];
        }
    };

    const handleLockColumn = () => {
        const newColumns = context.columnsState.map((column) => {
            if (column.id === props.column.id) {
                return {
                    ...column,
                    locked: !column.locked
                };
            }
            return column;
        });
        context.onColumnsChange(newColumns);
    };

    const useCheckboxFilter = checkboxFilterColumns.includes(props.column.id!);
    const useNotesFilter = props.column.id === 'hasNotes';

    // TODO: this should be refactored but don't bother, we're getting rid of this Grid control anyway...
    return (
        <div>
            {showColumnResetPopup && (
                <Dialog appendTo={multiWindow.root} title={'Reset columns'}>
                    <div>Do you want to reset all columns to default?</div>
                    <DialogActionsBar>
                        <Button
                            onClick={() => {
                                setShowColumnResetPopup(false);
                                context.onColumnsChange(context.columns.filter((x) => x.default));
                                onCloseMenu();
                            }}
                            themeColor="primary">
                            Yes
                        </Button>
                        <Button onClick={() => setShowColumnResetPopup(false)}>Cancel</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <GridColumnMenuSort {...props} />

            {props.filterable && (
                <>
                    {useCheckboxFilter ? (
                        <>
                            <ColumnMenuItem svgIcon={filterIcon} title="Filter" onClick={() => setExpandFilter((x) => !x)} />
                            <GridColumnMenuItemContent show={expandFilter}>
                                <ColumnCheckboxFilter data={getColumnFilterData()} {...props} />
                            </GridColumnMenuItemContent>
                        </>
                    ) : useNotesFilter ? (
                        <>
                            <ColumnMenuItem svgIcon={filterIcon} title="Filter" onClick={() => setExpandFilter((x) => !x)} />
                            <GridColumnMenuItemContent show={expandFilter}>
                                <NotesColumnFilters {...props} />
                            </GridColumnMenuItemContent>
                        </>
                    ) : (
                        <GridColumnMenuFilter {...props} />
                    )}
                </>
            )}

            <ColumnMenuItem
                svgIcon={filterClearIcon}
                title="Remove all filters"
                onClick={(e) => {
                    props.onFilterChange?.(null, e);
                    onCloseMenu();
                }}
            />
            <ColumnMenuItem
                svgIcon={props.column.locked ? unlockIcon : lockIcon}
                title={(props.column.locked ? 'Unlock' : 'Lock') + ' column'}
                onClick={handleLockColumn}
            />

            <ColumnMenuItem svgIcon={columnsIcon} title="Columns" onClick={() => setExpandColumnMenu((x) => !x)} />
            <GridColumnMenuItemContent show={expandedColumnMenu}>
                <GridColumnMenuColumnsList
                    columnsState={context.columnsState}
                    columns={context.columns}
                    onColumnsChange={context.onColumnsChange}
                    onCloseMenu={onCloseMenu}
                />
            </GridColumnMenuItemContent>

            <ColumnMenuItem svgIcon={undoIcon} title="Default columns" onClick={() => setShowColumnResetPopup(true)} />
        </div>
    );
};
